/**
 * What a page shows before the user has connected GitHub.
 *
 * Deliberately not an error state. Nothing has failed — this account simply has no GitHub
 * connection yet, which is the normal condition of every account for its first minute. The
 * page's job is to name the one action that changes that, not to apologise.
 *
 * It is driven by `github_connected` on the session rather than by catching a failed
 * request, so the prompt appears immediately instead of after a round trip that was always
 * going to be refused.
 */
import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { Github } from 'lucide-react'

import { EmptyState } from '@/components/ui/States'
import { useAuth } from '@/features/auth/useAuth'
import { ApiError } from '@/lib/api/errors'

/** The server's answer when a request needs a connection the account does not have. */
export function isIntegrationRequired(error: unknown): boolean {
  return error instanceof ApiError && error.errorType === 'INTEGRATION_REQUIRED'
}

export function ConnectGitHubPrompt({ what }: { what: string }) {
  return (
    <EmptyState
      icon={Github}
      title="Connect your GitHub account"
      hint={`${what} appear here once this account is connected to GitHub. Nothing is shared between accounts — you will only ever see your own.`}
      action={
        <Link
          to="/settings"
          className="inline-flex h-9 items-center gap-2 rounded-md border border-primary bg-primary
            px-4 text-sm font-medium text-ground shadow-card transition-colors
            hover:border-primary-bright hover:bg-primary-bright"
        >
          <Github aria-hidden="true" className="h-4 w-4" />
          Connect GitHub
        </Link>
      }
    />
  )
}

/**
 * Wraps a page's content, swapping in the prompt when there is no connection.
 *
 * ``error`` is accepted as well as the session flag because the two can disagree for a
 * moment — a connection revoked at GitHub is still `github_connected` until the session
 * refetches, and the request is what finds out first.
 */
export function ConnectGitHubGate({
  what,
  error,
  children,
}: {
  what: string
  error?: unknown
  children: ReactNode
}) {
  const { user } = useAuth()
  const missing = user ? !user.github_connected : false

  if (missing || isIntegrationRequired(error)) {
    return <ConnectGitHubPrompt what={what} />
  }
  return <>{children}</>
}
